import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Brain } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDeleteMemory } from '../../../hooks/useMemories';
import { apiErrorMessage } from '../../../lib/api/apiErrorMessage';
export function memorySaved(output) {
    if (!output || typeof output !== 'object') return null;
    const saved = output._memorySaved;
    if (!saved || typeof saved !== 'object') return null;
    const s = saved;
    return typeof s.id === 'string' && s.id ? s : null;
}
function where(s) {
    if (s.kind === 'team') return 'team memory';
    if (s.kind === 'project') {
        if (s.scopeKind === 'program') return "this program's memory";
        if (s.scopeKind === 'pursuit') return "this pursuit's memory";
        return "this proposal's memory";
    }
    return 'your memory';
}
/**
 * One quiet line under the answer: what the assistant kept and where. A new
 * personal or project note can be undone here; a team proposal waits for an
 * admin, and the line says so.
 */ export function MemorySavedLine({ saved }) {
    const del = useDeleteMemory();
    const [undone, setUndone] = useState(false);
    const [error, setError] = useState('');
    const title = saved.title?.trim() ? `"${saved.title.trim()}"` : 'a note';
    const pending = saved.status === 'pending';
    const canUndo = !saved.updated && !pending && saved.kind !== 'team';
    const tab = saved.kind === 'team' ? 'team' : 'notes';
    const undo = async ()=>{
        setError('');
        try {
            await del.mutateAsync({
                id: saved.id,
                scopeKind: saved.scopeKind ?? '',
                scopeId: saved.scopeId ?? ''
            });
            setUndone(true);
        } catch (err) {
            setError(apiErrorMessage(err, 'Could not undo.'));
        }
    };
    let text;
    if (undone) text = `Removed ${title} from ${where(saved)}.`;
    else if (pending) text = `Proposed ${title} to your team. It applies once an admin approves it.`;
    else text = `${saved.updated ? 'Updated' : 'Saved'} ${title} in ${where(saved)}.`;
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground",
        "data-testid": Testids.memory.savedLine,
        children: [
            /*#__PURE__*/ _jsx(Brain, {
                className: "h-3.5 w-3.5 shrink-0",
                "aria-hidden": true
            }),
            /*#__PURE__*/ _jsx("span", {
                children: text
            }),
            !undone && canUndo && /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "link",
                size: "inline",
                onClick: undo,
                disabled: del.isPending,
                "data-testid": Testids.memory.undoButton,
                children: "Undo"
            }),
            !undone && /*#__PURE__*/ _jsx(Link, {
                to: `?memory=${tab}`,
                className: "font-medium text-primary hover:underline",
                children: "View"
            }),
            error && /*#__PURE__*/ _jsx("span", {
                className: "text-destructive",
                children: error
            })
        ]
    });
}
